import { useCallback, useEffect } from "react";
import { uploadUrl } from "../../lib/assets";

/**
 * @param {{ lightbox: { list: import("../../data/albums").AlbumPhoto[], index: number } | null, onClose: () => void, onChangeIndex: (index: number) => void }} props
 */
export default function AlbumLightbox({ lightbox, onClose, onChangeIndex }) {
  const navigate = useCallback(
    (delta) => {
      if (!lightbox) return;
      const next =
        (lightbox.index + delta + lightbox.list.length) % lightbox.list.length;
      onChangeIndex(next);
    },
    [lightbox, onChangeIndex]
  );

  useEffect(() => {
    if (!lightbox) return undefined;

    const onKeyDown = (event) => {
      if (event.key === "Escape") onClose();
      else if (event.key === "ArrowLeft") navigate(-1);
      else if (event.key === "ArrowRight") navigate(1);
    };

    document.addEventListener("keydown", onKeyDown);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = "";
    };
  }, [lightbox, navigate, onClose]);

  if (!lightbox) return null;

  const photo = lightbox.list[lightbox.index];
  const indexLabel = `${String(lightbox.index + 1).padStart(2, "0")} / ${String(lightbox.list.length).padStart(2, "0")}`;

  return (
    <div
      className="album-lightbox"
      role="dialog"
      aria-modal="true"
      aria-label={photo.title}
      onClick={onClose}
    >
      <div className="album-lightbox__top">
        <span className="album-lightbox__index">{indexLabel}</span>
        <button
          type="button"
          className="album-lightbox__close"
          onClick={(event) => {
            event.stopPropagation();
            onClose();
          }}
        >
          CLOSE <span aria-hidden="true">×</span>
        </button>
      </div>

      <div
        className="album-lightbox__stage"
        onClick={(event) => event.stopPropagation()}
      >
        <div
          className="album-lightbox__image"
          style={
            photo.image
              ? { backgroundImage: `url('${uploadUrl(photo.image)}')` }
              : { background: photo.grad, backgroundImage: "none" }
          }
        />
      </div>

      <button
        type="button"
        className="album-lightbox__arrow album-lightbox__arrow--prev"
        aria-label="Previous photograph"
        onClick={(event) => {
          event.stopPropagation();
          navigate(-1);
        }}
      >
        ‹
      </button>
      <button
        type="button"
        className="album-lightbox__arrow album-lightbox__arrow--next"
        aria-label="Next photograph"
        onClick={(event) => {
          event.stopPropagation();
          navigate(1);
        }}
      >
        ›
      </button>

      <div
        className="album-lightbox__bottom"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="album-lightbox__caption">
          <div className="album-lightbox__title">{photo.title}</div>
          <div className="album-lightbox__subtitle">{photo.subtitle}</div>
        </div>
      </div>
    </div>
  );
}
